import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import jwt_decode, { JwtPayload } from 'jwt-decode';
import moment from 'moment';
import { generateHeaders } from 'utils/APIUtils';
import { AppDispatch, RootState } from 'store';
import { Reject } from '../Reject';
import { changePassword, saveFirstPassword, saveProfile, validateEmail } from './user';
import { handleApiError } from '../handleApiError';
import { decodeToken } from '../../utils/decodeToken';

export interface TokenData extends JwtPayload {
    data: {
        idUser: string;
        isComplete: boolean;
        firstname: string;
        lastname: string;
        email: string;
        emailVerified: boolean;
        idProfilePicture: number;
        latitude: string;
        longitude: string;
        notifications: boolean;
        terms: boolean;
        userFound: boolean;
        zoom: string;
        pictureUrl: string;
        picture: {
            idFile: string;
            url: string;
        };
        isSample?: boolean;
        superadmin?: boolean;
    };
}

interface AuthState {
    userLogingIn: boolean;
    userLoginError: boolean;
    userLoginSuccess: boolean;
    jwt: string;
    tokenData: TokenData;
    loginError: string;
    refreshingToken: boolean;
    tokenRefreshError: boolean;
    tokenRefreshSuccess: boolean;
}

const initialState: AuthState = {
    userLogingIn: false,
    userLoginError: false,
    userLoginSuccess: false,
    jwt: undefined,
    tokenData: undefined,
    loginError: undefined,
    refreshingToken: false,
    tokenRefreshError: false,
    tokenRefreshSuccess: false,
};

interface LoginParams {
    email: string;
    password: string;
}

interface LoginResponse {
    jwt: string;
    tokenData: TokenData;
}

interface AxiosLoginResponse {
    jwt: string;
}

type RefreshTokenResponse = LoginResponse

export const isTokenExpired = (exp: number | undefined): boolean => {
    if (!exp) return true;
    const tokenExpiration = new Date(exp * 1000);
    const now = new Date();
    return tokenExpiration.getTime() <= now.getTime();
};

export const shouldRefreshToken = (exp: number | undefined): boolean => {
    if (!exp)
        return false;

    const refreshThresholdSeconds = parseInt(process.env.REACT_APP_SESSION_REFRESH_SECONDS || '300');
    const tokenExpiration = new Date(exp * 1000);
    const now = new Date();
    const timeUntilExpiration = (tokenExpiration.getTime() - now.getTime()) / 1000;
    return timeUntilExpiration < refreshThresholdSeconds;
};

export const refreshToken = createAsyncThunk<RefreshTokenResponse, void, Reject>(
    'refreshToken',
    async (_, { rejectWithValue, getState }) => {
        const { auth } = getState() as RootState;
        const tokenExp = auth?.tokenData?.exp;

        if (!shouldRefreshToken(tokenExp)) {
            return rejectWithValue({ message: 'Token not yet ready for refresh' });
        }

        const headers = generateHeaders(auth.jwt);

        try {
            const response = await axios.post<{ jwt: string }>(
                `${process.env.REACT_APP_API_URL}/token`,
                {},
                { headers }
            );

            const tokenData = decodeToken(response.data.jwt);

            return {
                ...response.data,
                tokenData,
            };
        } catch (error) {
            const apiError = handleApiError(error);
            return rejectWithValue(apiError);
        }
    }
);

export const login = createAsyncThunk<LoginResponse, LoginParams, Reject>(
    'login',
    async (params, { rejectWithValue }) => {
        try {
            const response = await axios.post<AxiosLoginResponse>(
                `${process.env.REACT_APP_API_URL}/login`,
                {
                    email: params.email,
                    password: params.password,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.data || !response.data.jwt) {
                throw new Error('JWT is missing');
            }

            try {
                const tokenData = jwt_decode(response.data.jwt);
                console.log('tokenData', tokenData);
                return {
                    ...response.data,
                    tokenData,
                } as LoginResponse;
            } catch (decodeError) {
                console.error('Error decoding JWT:', decodeError);
                throw new Error('Failed to decode JWT');
            }
        } catch (error) {
            const apiError = handleApiError(error);
            return rejectWithValue(apiError);
        }
    }
);

export const loginWithGoogle = createAsyncThunk<LoginResponse, string, Reject>(
    'loginWithGoogle',
    async (credential, { rejectWithValue }) => {
        try {
            const response = await axios.post<AxiosLoginResponse>(
                `${process.env.REACT_APP_API_URL}/auth/google`,
                { credential },
                { headers: { 'Content-Type': 'application/json' } }
            );

            if (!response.data || !response.data.jwt) {
                throw new Error('JWT is missing');
            }

            const tokenData = decodeToken(response.data.jwt);
            return { ...response.data, tokenData } as LoginResponse;
        } catch (error) {
            const apiError = handleApiError(error);
            return rejectWithValue(apiError);
        }
    }
);

export const acceptTerms = createAsyncThunk<LoginResponse, void, Reject>(
    'acceptTerms',
    async (_, { getState, rejectWithValue }) => {
        const { auth } = getState() as RootState;
        try {
            const response = await axios.put<AxiosLoginResponse>(
                `${process.env.REACT_APP_API_URL}/user/terms`,
                {},
                { headers: generateHeaders(auth.jwt) }
            );
            const tokenData = decodeToken(response.data.jwt);
            return { ...response.data, tokenData } as LoginResponse;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const logoutIfSampleUser = () => (dispatch: AppDispatch, getState: () => RootState) => {
    console.log('Logging out sample user');
    const state = getState();
    const isSample = state.auth.tokenData?.data?.isSample;
    if (isSample) {
        dispatch(logout());
    }
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: () => {
            return { ...initialState };
        },
        sampleUser: (state) => {
            const now = new Date();
            now.setDate(now.getDate() + 1);
            state.userLogingIn = true;
            state.userLoginSuccess = true;
            state.jwt = 'sampleToken'
            state.tokenData = {
                exp: now.getTime(),
                iat: now.getTime(),
                data: {
                    email: 'sample@kwadhub.net',
                    firstname: 'John',
                    lastname: 'Doe',
                    terms: true,
                    isComplete: true,
                    emailVerified: true,
                    idUser: 'sampleUser',
                    latitude: '-33.865143',
                    longitude: '151.209900',
                    zoom: '12',
                    idProfilePicture: undefined,
                    notifications: undefined,
                    userFound: true,
                    pictureUrl: undefined,
                    picture: undefined,
                    isSample: true,
                }
            };
        },
        validateCurrentUser: (state) => {
            const { tokenData } = state;
            if (tokenData && tokenData.exp) {
                const expiration = moment(tokenData.exp * 1000);
                const now = moment();
                if (expiration.isBefore(now)) {
                    console.log('Token expired...');
                    authSlice.caseReducers.logout();
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(login.pending, (state) => {
                state.userLogingIn = true;
                state.userLoginError = false;
                state.userLoginSuccess = false;
                state.jwt = undefined;
                state.tokenData = undefined;
                state.loginError = undefined;
            })
            .addCase(login.rejected, (state, action) => {
                state.userLogingIn = false;
                state.userLoginError = true;
                state.userLoginSuccess = false;
                state.jwt = undefined;
                state.tokenData = undefined;
                state.loginError = action.payload?.message ?? '';
            })
            .addCase(login.fulfilled, (state, action) => {
                state.userLogingIn = false;
                state.userLoginError = false;
                state.userLoginSuccess = true;
                state.jwt = action.payload.jwt;
                state.tokenData = action.payload.tokenData;
                state.loginError = undefined;
            })
            .addCase(refreshToken.pending, (state) => {
                state.refreshingToken = true;
                state.tokenRefreshError = false;
                state.tokenRefreshSuccess = false;
            })
            .addCase(refreshToken.rejected, (state) => {
                state.tokenRefreshError = true;
                state.refreshingToken = false;
                state.tokenRefreshSuccess = false;
            })
            .addCase(refreshToken.fulfilled, (state, action) => {
                state.refreshingToken = false;
                state.tokenRefreshError = false;
                state.tokenRefreshSuccess = true;
                state.jwt = action.payload.jwt;
                state.tokenData = action.payload.tokenData;
            })
            .addCase(loginWithGoogle.pending, (state) => {
                state.userLogingIn = true;
                state.userLoginError = false;
                state.userLoginSuccess = false;
                state.jwt = undefined;
                state.tokenData = undefined;
                state.loginError = undefined;
            })
            .addCase(loginWithGoogle.rejected, (state, action) => {
                state.userLogingIn = false;
                state.userLoginError = true;
                state.userLoginSuccess = false;
                state.loginError = action.payload?.message ?? '';
            })
            .addCase(loginWithGoogle.fulfilled, (state, action) => {
                state.userLogingIn = false;
                state.userLoginError = false;
                state.userLoginSuccess = true;
                state.jwt = action.payload.jwt;
                state.tokenData = action.payload.tokenData;
                state.loginError = undefined;
            })
            .addCase(validateEmail.fulfilled, (state, action) => {
                // Other action that will get the user logged in is to confirm the email with the link.
                state.userLogingIn = false;
                state.userLoginError = false;
                state.userLoginSuccess = true;
                delete state.loginError;
                state.jwt = action.payload.jwt;
                state.tokenData = action.payload.tokenData;
            })
            .addCase(saveFirstPassword.fulfilled, (state, action) => {
                state.userLogingIn = false;
                state.userLoginError = false;
                state.userLoginSuccess = true;
                state.jwt = action.payload.jwt;
                state.tokenData = action.payload.tokenData;
            })
            .addCase(saveProfile.fulfilled, (state, action) => {
                state.jwt = action.payload.jwt;
                state.tokenData.data = {
                    ...state.tokenData.data,
                    firstname: action.payload.firstname,
                    lastname: action.payload.lastname,
                    picture: action.payload.picture,
                };
            })
            .addCase(changePassword.fulfilled, (state, action) => {
                state.jwt = action.payload.jwt;
                state.tokenData = action.payload.tokenData;
            })
            .addCase(acceptTerms.fulfilled, (state, action) => {
                state.jwt = action.payload.jwt;
                state.tokenData = action.payload.tokenData;
            });
    },
});

export const { logout, validateCurrentUser, sampleUser } = authSlice.actions;
export default authSlice.reducer;

export const LOGOUT = logout.toString();
